// project/src/lib/clonedVoicesApi.ts
//
// Helpers para la feature de Voice Cloning con ElevenLabs IVC.
//
// Flow general:
//   1. Cliente sube el audio sample a Supabase Storage (bucket `voces`)
//      en el path `<id_company>/<filename>.<ext>`.
//   2. Cliente POST al webhook n8n `clonar_voz_propia` con metadata + path.
//   3. El workflow descarga el sample, lo manda a ElevenLabs IVC,
//      hace INSERT en `voces_clonadas` y devuelve el `voice_id`.
//
// Tabla `voces_clonadas`: id (uuid), id_company (uuid), nombre (text),
// elevenlabs_voice_id (text), sample_file_name (text),
// sample_duration_sec (int), consentimiento_aceptado_at (timestamptz),
// created_at (timestamptz).
//
// Bucket Storage `voces`: `<id_company>/<filename>` — NO público, URLs
// firmadas con TTL 1h.

import { supabase } from './supabase';
import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import {
  MIN_SAMPLE_DURATION_SEC,
  MAX_SAMPLE_DURATION_SEC,
  MAX_SAMPLE_SIZE_BYTES,
  ALLOWED_SAMPLE_EXTENSIONS,
} from '../constants/voiceCloning';

const BUCKET = 'voces';
const CLONAR_VOZ_URL =
  (import.meta.env.VITE_CLONAR_VOZ_URL as string | undefined) ||
  'https://ai-agency-dt7d.onrender.com/webhook/clonar_voz_propia';

// ─── Tipos ────────────────────────────────────────────────────────────

export interface ClonedVoice {
  id: string;
  nombre: string;
  elevenlabsVoiceId: string;
  createdAt: string;
}

export type CloneVoiceErrorType =
  | 'audio_too_short'
  | 'audio_too_long'
  | 'audio_too_large'
  | 'audio_invalid_format'
  | 'name_empty'
  | 'consent_required'
  | 'upload_failed'
  | 'plan_required'
  | 'quota_exceeded'
  | 'audio_quality'
  | 'service_unavailable'
  | 'technical';

const ERROR_COPY: Record<CloneVoiceErrorType, string> = {
  audio_too_short: 'El audio debe durar al menos 30 segundos.',
  audio_too_long: 'El audio no puede superar los 3 minutos.',
  audio_too_large: 'El archivo no puede pesar más de 10 MB.',
  audio_invalid_format:
    'Formato no soportado. Usá MP3, M4A, WAV, OGG o WebM.',
  name_empty: 'Ponele un nombre a tu voz.',
  consent_required: 'Aceptá los términos para continuar.',
  upload_failed: 'No pudimos subir el audio. Intentá de nuevo.',
  plan_required: 'Esta función requiere plan Pro o Ultra.',
  quota_exceeded:
    'Llegaste al máximo de voces clonadas. Contactanos para liberar espacio.',
  audio_quality:
    'El audio no tiene calidad suficiente. Probá grabar en un lugar silencioso.',
  service_unavailable:
    'ElevenLabs no está disponible. Probá en unos minutos.',
  technical: 'Hubo un problema técnico. Intentá de nuevo.',
};

export function mapErrorTypeToMessage(t: CloneVoiceErrorType): string {
  return ERROR_COPY[t] ?? ERROR_COPY.technical;
}

export class CloneVoiceError extends Error {
  public readonly errorType: CloneVoiceErrorType;
  public readonly raw: string | null;
  constructor(
    errorType: CloneVoiceErrorType,
    message: string,
    raw: string | null = null,
  ) {
    super(message);
    this.name = 'CloneVoiceError';
    this.errorType = errorType;
    this.raw = raw;
  }
}

// ─── Fetch ────────────────────────────────────────────────────────────

/**
 * Lista las voces clonadas de una empresa. Ordenadas por created_at DESC
 * (más recientes primero).
 */
export async function fetchClonedVoices(
  companyId: string,
): Promise<ClonedVoice[]> {
  const { data, error } = await supabase
    .from('voces_clonadas')
    .select('id, nombre, elevenlabs_voice_id, created_at')
    .eq('id_company', companyId)
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[clonedVoicesApi] fetchClonedVoices error', error);
    return [];
  }
  return (data ?? []).map(
    (row: any): ClonedVoice => ({
      id: row.id,
      nombre: row.nombre,
      elevenlabsVoiceId: row.elevenlabs_voice_id,
      createdAt: row.created_at,
    }),
  );
}

// ─── Upload + Create ──────────────────────────────────────────────────

function buildSampleFileName(seed: string, ext: string): string {
  const slug = seed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40);
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${
    slug || 'voz'
  }.${ext}`;
}

function getFileExtension(fileName: string): string {
  const m = fileName.match(/\.([a-zA-Z0-9]+)$/);
  return m ? m[1].toLowerCase() : 'webm';
}

/**
 * Sube el sample a Supabase Storage bajo `voces/<companyId>/<filename>`.
 * Devuelve el path completo (lo necesita el webhook para descargar).
 */
async function uploadVoiceSample({
  companyId,
  audioBlob,
  nombre,
  originalExt,
}: {
  companyId: string;
  audioBlob: Blob;
  nombre: string;
  originalExt: string;
}): Promise<{ path: string; fileName: string }> {
  const fileName = buildSampleFileName(nombre, originalExt);
  const path = `${companyId}/${fileName}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, audioBlob, {
      cacheControl: '3600',
      upsert: false,
      contentType: audioBlob.type || `audio/${originalExt}`,
    });
  if (error) {
    throw new CloneVoiceError(
      'upload_failed',
      mapErrorTypeToMessage('upload_failed'),
      error.message,
    );
  }
  return { path, fileName };
}

interface WebhookSuccessResponse {
  success: true;
  voice_id: string;
  voice_db_id: string;
}

interface WebhookErrorResponse {
  success: false;
  error_type: CloneVoiceErrorType;
  error_message?: string;
}

/**
 * Crea una voz clonada end-to-end:
 *   1. Valida input cliente (nombre, duración, formato, consentimiento)
 *   2. Sube el sample a Supabase Storage
 *   3. POST al webhook n8n con metadata + path
 *   4. Si el webhook tira error, lo traduce a CloneVoiceError tipado
 *   5. Devuelve la ClonedVoice ya guardada
 *
 * Si falla DESPUÉS de subir el sample, el archivo queda huérfano en
 * Storage. No hacemos cleanup automático (KISS v1).
 */
export async function createClonedVoice({
  userId,
  companyId,
  nombre,
  audioBlob,
  audioFileName,
  durationSec,
  consentimientoAceptado,
}: {
  userId: string;
  companyId: string;
  nombre: string;
  audioBlob: Blob;
  audioFileName: string;
  durationSec: number;
  consentimientoAceptado: boolean;
}): Promise<ClonedVoice> {
  // 1. Validaciones cliente
  const trimmedNombre = nombre.trim();
  if (!trimmedNombre) {
    throw new CloneVoiceError(
      'name_empty',
      mapErrorTypeToMessage('name_empty'),
    );
  }
  if (!consentimientoAceptado) {
    throw new CloneVoiceError(
      'consent_required',
      mapErrorTypeToMessage('consent_required'),
    );
  }
  if (durationSec < MIN_SAMPLE_DURATION_SEC) {
    throw new CloneVoiceError(
      'audio_too_short',
      mapErrorTypeToMessage('audio_too_short'),
    );
  }
  if (durationSec > MAX_SAMPLE_DURATION_SEC) {
    throw new CloneVoiceError(
      'audio_too_long',
      mapErrorTypeToMessage('audio_too_long'),
    );
  }
  if (audioBlob.size > MAX_SAMPLE_SIZE_BYTES) {
    throw new CloneVoiceError(
      'audio_too_large',
      mapErrorTypeToMessage('audio_too_large'),
    );
  }
  const ext = getFileExtension(audioFileName);
  if (!ALLOWED_SAMPLE_EXTENSIONS.includes(`.${ext}`)) {
    throw new CloneVoiceError(
      'audio_invalid_format',
      mapErrorTypeToMessage('audio_invalid_format'),
    );
  }

  // 2. Upload del sample
  const { path: samplePath, fileName: sampleFileName } =
    await uploadVoiceSample({
      companyId,
      audioBlob,
      nombre: trimmedNombre,
      originalExt: ext,
    });

  // 3. POST al backend (atlas o n8n según flag — ADR-606). El sample_path ya
  // arranca con el prefijo de la empresa (requisito del atlas): lo arma
  // uploadVoiceSample como `<companyId>/...`.
  let res: Response;
  try {
    if (USE_ATLAS_BACKEND) {
      res = await atlasBackendFetch('/v1/audio/clone-voice', {
        nombre: trimmedNombre,
        sample_path: samplePath,
        sample_file_name: sampleFileName,
        sample_duration_sec: Math.round(durationSec),
        consentimiento_aceptado: true,
      });
    } else {
      res = await fetch(CLONAR_VOZ_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          user_id: userId,
          nombre: trimmedNombre,
          sample_path: samplePath,
          sample_file_name: sampleFileName,
          sample_duration_sec: Math.round(durationSec),
          consentimiento_aceptado: true,
        }),
      });
    }
  } catch (e: any) {
    throw new CloneVoiceError(
      'technical',
      mapErrorTypeToMessage('technical'),
      e?.message ?? 'network_error',
    );
  }

  // OJO: los errores tipados del atlas vienen con HTTP 403/422 pero el MISMO
  // body {success:false, error_type, error_message} que n8n mandaba — parsear
  // ANTES de cortar por status, así el mapeo tipado funciona en ambos paths.
  // Solo un body no-JSON (5xx raro / HTML) cae a technical.
  let rawText: string | null = null;
  let data: WebhookSuccessResponse | WebhookErrorResponse | null = null;
  try {
    rawText = await res.text();
    data = JSON.parse(rawText) as WebhookSuccessResponse | WebhookErrorResponse;
  } catch {
    data = null;
  }
  if (data == null) {
    throw new CloneVoiceError(
      'technical',
      mapErrorTypeToMessage('technical'),
      rawText || `HTTP ${res.status}`,
    );
  }

  if (!data || (data as any).success !== true) {
    const errData = data as WebhookErrorResponse;
    const t = (errData?.error_type ?? 'technical') as CloneVoiceErrorType;
    throw new CloneVoiceError(
      t,
      mapErrorTypeToMessage(t),
      errData?.error_message ?? null,
    );
  }

  const success = data as WebhookSuccessResponse;

  // 5. Devolver ClonedVoice
  return {
    id: success.voice_db_id,
    nombre: trimmedNombre,
    elevenlabsVoiceId: success.voice_id,
    createdAt: new Date().toISOString(),
  };
}
